// Tracks confirmed message IDs and failed acknowledgements. No DOM or HTTP client.
export default class ReceiptTracker {
  #sendReceipt;
  #scheduler;
  #retryDelay;
  #onRead;
  #active;
  #states;
  constructor({ sendReceipt, scheduler, retryDelay, onRead }) {
    this.#sendReceipt = sendReceipt;
    this.#scheduler = scheduler;
    this.#retryDelay = retryDelay;
    this.#onRead = onRead;
    this.#active = false;
    this.#states = [false, true].map(() => ({ done: new Set(), pending: new Set(), failed: new Set(), timer: null, attempts: 0 }));
  }

  start() { this.#active = true; }
  hasRead(id) { return this.#states[1].done.has(id); }

  recordSnapshot(messages) {
    for (const message of messages) {
      if (message.delivered_at) this.#states[0].done.add(message.id);
      if (message.read_at) this.#states[1].done.add(message.id);
    }
    for (const state of this.#states) {
      this.#prune(state);
      if (!state.failed.size) this.#scheduler.clear(state.timer);
    }
  }

  #prune(state) {
    for (const id of state.failed) if (state.done.has(id)) state.failed.delete(id);
  }

  async acknowledge(ids, viewed) {
    if (!this.#active) return;
    const state = this.#states[viewed ? 1 : 0];
    this.#prune(state);
    const fresh = [...new Set(ids)].filter(id => Number.isInteger(id) && id > 0 && !state.done.has(id) && !state.pending.has(id));
    // Reserve the entire operation, including later batches, before yielding.
    // Another visibility/snapshot update must not enqueue these IDs again.
    fresh.forEach(id => state.pending.add(id));
    try {
      for (let offset = 0; offset < fresh.length && this.#active; offset += 500) {
        const batch = fresh.slice(offset, offset + 500);
        try {
          await this.#sendReceipt(batch, viewed);
          batch.forEach(id => { state.done.add(id); state.failed.delete(id); });
          state.attempts = 0;
          if (viewed && this.#active) this.#onRead();
        } catch { batch.forEach(id => state.failed.add(id)); }
        finally { batch.forEach(id => state.pending.delete(id)); }
      }
    } finally {
      fresh.forEach(id => state.pending.delete(id));
    }
    this.#scheduler.clear(state.timer);
    this.#prune(state);
    if (this.#active && state.failed.size) {
      state.timer = this.#scheduler.set(() => this.acknowledge([...state.failed], viewed), this.#retryDelay(state.attempts++));
    }
  }

  dispose() {
    this.#active = false;
    this.#states.forEach(state => this.#scheduler.clear(state.timer));
  }
}
